import React from "react";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import {
  Panel,
  PanelBody,
  PanelHeader,
} from "../../components/panel/panel.jsx";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import ModalContainer from "../../components/ModalContainer.tsx";
import EmptyImage from "../../components/EmptyImage.tsx";
import {
  findDeductionTemplates,
  saveDeductionTemplate,
  deleteDeductionTemplate,
  updateDeductionTemplate,
  DeductionTemplate,
  CreateDeductionTemplateDto,
} from "../../services/deductions.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import Loader from "../../components/Loader.tsx";
import TemplateForm from "../../components/deduction/TemplateForm.tsx";

const TemplateListPage = () => {
  const [loading, setLoading] = React.useState(false);
  const [templates, setTemplates] = React.useState<DeductionTemplate[]>([]);
  const [modal, setModal] = React.useState(false);
  const [editModal, setEditModal] = React.useState(false);
  const [deleteModal, setDeleteModal] = React.useState(false);
  const [selectedTemplate, setSelectedTemplate] = React.useState<DeductionTemplate | null>(null);
  const [sortBy, setSortBy] = React.useState<string>("");
  const [defaultSort, setDefaultSort] = React.useState<"asc" | "desc">("asc");

  useAppSettings();

  const formatMoney = (val: number) => {
    const num = Number(val);
    if (Number.isNaN(num)) return "-";
    return num.toLocaleString("es-MX", {
      style: "currency",
      currency: "MXN",
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (date: string) => {
    if (!date || date === "-" || date === "" || date === "Invalid Date")
      return "-";

    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return "-";

      return dateObj.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return "-";
    }
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const getIconSort = () => {
    return (
      <i
        className={`fa-solid fa-sort-${defaultSort === "asc" ? "up" : "down"}`}
      />
    );
  };

  const setSort = (sort: string) => {
    const sortOrder =
      sortBy !== sort ? "asc" : defaultSort === "asc" ? "desc" : "asc";

    setSortBy(sort);
    setDefaultSort(sortOrder);
  };

  const sortData = (data: DeductionTemplate[]) => {
    if (!sortBy) return data;

    return [...data].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortBy) {
        case "name":
          aValue = (a.name || "").toLowerCase();
          bValue = (b.name || "").toLowerCase();
          break;
        case "description":
          aValue = (a.description || "").toLowerCase();
          bValue = (b.description || "").toLowerCase();
          break;
        case "amount":
          aValue = Number(a.amount) || 0;
          bValue = Number(b.amount) || 0;
          break;
        case "created":
          aValue = new Date(a.createdAt || "").getTime();
          bValue = new Date(b.createdAt || "").getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return defaultSort === "asc" ? -1 : 1;
      if (aValue > bValue) return defaultSort === "asc" ? 1 : -1;
      return 0;
    });
  };

  const fetchTemplates = async () => {
    console.log("Fetching templates...");
    setLoading(true);
    try {
      const resp = await findDeductionTemplates();
      console.log("Templates fetched:", resp);
      setTemplates(resp || []);
    } catch (e) {
      console.error(e);
      notification("Error ⚠️", "Error al cargar las plantillas", "danger");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchTemplates();
  }, []);

  const handleOpenModal = () => {
    setModal(true);
  };

  const handleCloseModal = () => {
    setModal(false);
    setSelectedTemplate(null);
  };

  const handleEdit = (template: DeductionTemplate) => {
    setSelectedTemplate(template);
    setEditModal(true);
  };

  const handleDelete = (template: DeductionTemplate) => {
    setSelectedTemplate(template);
    setDeleteModal(true);
  };

  const handleCloseEditModal = () => {
    setEditModal(false);
    setSelectedTemplate(null);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
    setSelectedTemplate(null);
  };

  const handleDeleteSuccess = async () => {
    if (!selectedTemplate) return;

    try {
      setLoading(true);
      await deleteDeductionTemplate(selectedTemplate.id);
      notification("", "Plantilla eliminada exitosamente", "success");
      fetchTemplates();
      handleCloseDeleteModal();
    } catch (error) {
      notification("Error ⚠️", "Error al eliminar la plantilla", "danger");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSuccess = () => {
    fetchTemplates();
    handleCloseModal();
  };

  const handleEditSuccess = () => {
    fetchTemplates();
    handleCloseEditModal();
  };

  const sortedTemplates = React.useMemo(
    () => sortData(templates),
    [templates, sortBy, defaultSort]
  );

  return (
    <Layout loading={loading}>
      <Title
        baseTitle="Nómina"
        basePath="/nomina"
        activeTitle="Plantillas de Deducciones"
        title={"Plantillas de Deducciones"}
      />

      <div className="card border-0 m-4 rounded">
        <Panel>
          <PanelHeader noButton={true} className="bg-azul">
            <div className="d-flex justify-content-between align-items-center w-100">
              <span>Mis Plantillas</span>
              <button
                className="btn btn-warning btn-sm"
                onClick={handleOpenModal}
              >
                <i className="fa fa-plus me-2"></i>
                Nueva Plantilla
              </button>
            </div>
          </PanelHeader>
          <PanelBody>
            <div className="p-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="text-muted small">
                  {`${templates.length} plantillas guardadas`}
                </div>
              </div>

              <div
                className="table-responsive mb-3"
                style={{
                  overflowY: "auto",
                  maxHeight: "600px",
                  minHeight: "400px",
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #e9ecef",
                  borderRadius: "8px",
                }}
              >
                <table className="table table-striped table-hover table-panel text-nowrap align-middle mb-0">
                  <thead>
                    <tr
                      style={{
                        position: "sticky",
                        top: 0,
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      <th
                        className="text-uppercase text-muted small py-3 px-3"
                        style={{
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          cursor: "pointer",
                          width: "30%",
                        }}
                        onClick={() => setSort("name")}
                      >
                        Nombre {sortBy === "name" && getIconSort()}
                      </th>
                      <th
                        className="text-uppercase text-muted small py-3 px-3"
                        style={{
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          cursor: "pointer",
                          width: "25%",
                          maxWidth: "200px",
                        }}
                        onClick={() => setSort("description")}
                      >
                        Descripción{" "}
                        {sortBy === "description" && getIconSort()}
                      </th>
                      <th
                        className="text-uppercase text-muted small py-3 px-3"
                        style={{
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          cursor: "pointer",
                          width: "15%",
                        }}
                        onClick={() => setSort("amount")}
                      >
                        Monto {sortBy === "amount" && getIconSort()}
                      </th>
                      <th
                        className="text-uppercase text-muted small py-3 px-3"
                        style={{
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          cursor: "pointer",
                          width: "15%",
                        }}
                        onClick={() => setSort("created")}
                      >
                        Fecha creación{" "}
                        {sortBy === "created" && getIconSort()}
                      </th>
                      <th
                        className="text-uppercase text-muted small py-3 px-3"
                        style={{ 
                          fontWeight: 600, 
                          fontSize: "0.75rem",
                          width: "15%"
                        }}
                      >
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTemplates.map((template, idx) => (
                      <tr
                        key={template.id || idx}
                        style={{ borderBottom: "1px solid #f1f3f4" }}
                      >
                        <td 
                          className="py-3 px-3 fw-medium"
                          style={{ width: "30%" }}
                        >
                          {template.name}
                        </td>
                        <td 
                          className="py-3 px-3"
                          style={{
                            width: "25%",
                            maxWidth: "200px",
                            wordWrap: "break-word",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}
                          title={template.description}
                        >
                          {truncateText(template.description, 100)}
                        </td>
                        <td 
                          className="py-3 px-3 fw-medium"
                          style={{ width: "15%" }}
                        >
                          {formatMoney(template.amount)}
                        </td>
                        <td 
                          className="py-3 px-3"
                          style={{ width: "15%" }}
                        >
                          {formatDate(template.createdAt)}
                        </td>
                        <td 
                          className="py-3 px-3"
                          style={{ width: "15%" }}
                        >
                          <button
                            className="btn btn-sm btn-info me-1"
                            onClick={() => handleEdit(template)}
                            title="Editar plantilla"
                          >
                            <i className="fa fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(template)}
                            title="Eliminar plantilla"
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                    {templates.length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          className="text-center text-muted py-5"
                        >
                          <div className="d-flex align-items-center justify-content-center">
                            <div className="d-flex flex-column align-items-center justify-content-center">
                              <EmptyImage className="mx-auto w-250px" />
                              <h5 className="text-secondary text-center fs-20px mt-3">
                                No hay plantillas guardadas
                              </h5>
                              <p className="text-muted">
                                Crea tu primera plantilla para reutilizar configuraciones frecuentes
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </PanelBody>
        </Panel>
      </div>

      {/* Modal para crear nueva plantilla */}
      <ModalContainer
        open={modal}
        closeFn={handleCloseModal}
        title="Nueva Plantilla"
        size="md"
      >
        <TemplateForm
          close={handleCloseModal}
          onSuccess={handleCreateSuccess}
        />
      </ModalContainer>

      {/* Modal para editar plantilla */}
      <ModalContainer
        open={editModal}
        closeFn={handleCloseEditModal}
        title="Editar Plantilla"
        size="md"
      >
        <TemplateForm
          close={handleCloseEditModal}
          onSuccess={handleEditSuccess}
          templateToEdit={selectedTemplate}
          isEditing={true}
        />
      </ModalContainer>

      {/* Modal para confirmar eliminación */}
      <ModalContainer
        open={deleteModal}
        closeFn={handleCloseDeleteModal}
        title="Eliminar Plantilla"
        size="sm"
      >
        <div className="p-4">
          <div className="alert alert-warning mb-3" role="alert">
            <i className="fa fa-exclamation-triangle me-2"></i>
            <strong>¿Estás seguro?</strong>
          </div>
          <p className="mb-3">
            Se eliminará permanentemente la plantilla{" "}
            <strong>"{selectedTemplate?.name}"</strong>. Esta acción no se puede deshacer.
          </p>
          <div className="d-flex justify-content-end gap-2">
            <button
              className="btn btn-secondary"
              onClick={handleCloseDeleteModal}
            >
              Cancelar
            </button>
            <button
              className="btn btn-danger"
              onClick={handleDeleteSuccess}
            >
              <i className="fa fa-trash me-1"></i>
              Eliminar
            </button>
          </div>
        </div>
      </ModalContainer>
    </Layout>
  );
};

export default TemplateListPage;
